import { secureGet, securePost, securePut, securePatch } from '../utils/HttpUtils';
import { Share, ShareFilters, ShareFormMetadata, ShareInput } from '../types/interfaces';

const apiUrls = {
  shares: '/v1/private/guarantees/shares',
  formMetadata: '/v1/private/guarantees/form-metadata/share',
};

const mapShare = (item: any): Share => ({
  id: item.id,
  name: item.name,
  ticker: item.ticker,
  shareTypeId: item.share_type_id,
  shareTypeName: item.share_type_name,
  currencyId: item.currency_id,
  currencyCode: item.currency_code,
  currencyDescription: item.currency_description,
  nominalValue: item.nominal_value,
  issuedQuantity: item.issued_quantity,
  issueDate: item.issue_date,
  enabled: item.enabled,
});

const toApiBody = (input: ShareInput) => ({
  name: input.name,
  ticker: input.ticker,
  share_type_id: input.shareTypeId,
  currency_id: input.currencyId,
  nominal_value: input.nominalValue,
  issued_quantity: input.issuedQuantity,
  issue_date: input.issueDate,
  enabled: input.enabled,
});

const buildSharesUrl = (filters: ShareFilters): string => {
  const params = new URLSearchParams();
  if (filters.search) params.append('search', filters.search);
  if (filters.shareTypeId !== null && filters.shareTypeId !== undefined) {
    params.append('share_type_id', String(filters.shareTypeId));
  }
  if (filters.enabled !== null && filters.enabled !== undefined) {
    params.append('enabled', String(filters.enabled));
  }
  const query = params.toString();
  return query ? `${apiUrls.shares}?${query}` : apiUrls.shares;
};

export const getShares = async (
  filters: ShareFilters,
): Promise<{ success: boolean; data?: Share[]; message?: string; status?: number }> => {
  try {
    const response = await secureGet(buildSharesUrl(filters));
    if (response.success && 'data' in response) {
      return {
        success: true,
        data: (response.data as any[]).map(mapShare),
      };
    }
    return {
      success: false,
      message: (response as any).message || 'Error al obtener las acciones',
      status: (response as any).status,
    };
  } catch (error) {
    return {
      success: false,
      message: 'Error al obtener las acciones',
    };
  }
};

export const createShare = async (
  input: ShareInput,
): Promise<{ success: boolean; data?: Share; message?: string; status?: number }> => {
  try {
    const response = await securePost(apiUrls.shares, toApiBody(input));
    if (response.success && 'data' in response) {
      return {
        success: true,
        data: mapShare(response.data),
      };
    }
    return {
      success: false,
      message: (response as any).message || 'Error al crear la acción',
      status: (response as any).status,
    };
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || 'Error al crear la acción',
      status: error?.response?.status,
    };
  }
};

export const updateShare = async (
  id: number,
  input: ShareInput,
): Promise<{ success: boolean; data?: Share; message?: string; status?: number }> => {
  try {
    const response = await securePut(`${apiUrls.shares}/${id}`, toApiBody(input));
    if (response.success && 'data' in response) {
      return {
        success: true,
        data: mapShare(response.data),
      };
    }
    return {
      success: false,
      message: (response as any).message || 'Error al actualizar la acción',
      status: (response as any).status,
    };
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || 'Error al actualizar la acción',
      status: error?.response?.status,
    };
  }
};

export const setShareEnabled = async (
  id: number,
  enabled: boolean,
): Promise<{ success: boolean; data?: Share; message?: string; status?: number }> => {
  try {
    const response = await securePatch(`${apiUrls.shares}/${id}/enabled`, { enabled });
    if (response.success && 'data' in response) {
      return {
        success: true,
        data: mapShare(response.data),
      };
    }
    return {
      success: false,
      message: (response as any).message || 'Error al cambiar el estado de la acción',
      status: (response as any).status,
    };
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || 'Error al cambiar el estado de la acción',
      status: error?.response?.status,
    };
  }
};

export const getShareFormMetadata = async (): Promise<{
  success: boolean;
  data?: ShareFormMetadata;
  message?: string;
  status?: number;
}> => {
  try {
    const response = await secureGet(apiUrls.formMetadata);
    if (response.success && 'data' in response) {
      const raw = response.data as any;
      return {
        success: true,
        data: {
          shareTypes: (raw.share_types || []).map((t: any) => ({ id: t.id, name: t.name })),
          currencies: (raw.currencies || []).map((c: any) => ({
            id: c.id,
            code: c.code,
            label: c.label,
            description: c.description,
          })),
        },
      };
    }
    return {
      success: false,
      message: (response as any).message || 'Error al obtener los metadatos del formulario',
      status: (response as any).status,
    };
  } catch (error) {
    return {
      success: false,
      message: 'Error al obtener los metadatos del formulario',
    };
  }
};
